import { useEffect, useState, useCallback } from 'react';
import {
  getOrCreateThread,
  getThreadMessages,
  sendMessage as sendMessageApi,
} from '../api/expertise-detail.api';
import {
  ChatThread,
  ChatMessage,
  UseChatReturn,
} from '../types/expertise-detail.types';

export const useChat = (affaireId: string | undefined): UseChatReturn => {
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // Initialize thread and fetch messages
  useEffect(() => {
    if (!affaireId) {
      setLoading(false);
      setError('No affaire ID provided');
      return;
    }

    const initializeChat = async () => {
      setLoading(true);
      setError(null);

      try {
        // Get or create thread
        const threadRes = await getOrCreateThread(affaireId);

        if (!threadRes.success || !threadRes.data) {
          throw new Error(threadRes.message || 'Failed to get/create thread');
        }

        const threadData = threadRes.data.thread;
        setThread(threadData);

        // Fetch messages for the thread
        const messagesRes = await getThreadMessages(threadData.id);

        if (messagesRes.success && messagesRes.data) {
          setMessages(messagesRes.data.messages || []);
        } else {
          console.warn('Failed to fetch messages:', messagesRes.message);
          setMessages([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error initializing chat:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize chat');
        setLoading(false);
      }
    };

    initializeChat();
  }, [affaireId]);

  // Send a message
  const sendMessage = useCallback(async (text: string) => {
    if (!thread || !text.trim()) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      const response = await sendMessageApi(thread.id, {
        message_text: text.trim(),
        message_type: 'text',
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to send message');
      }

      // Add the new message to the list
      if (response.data?.message) {
        setMessages(prev => [...prev, response.data!.message]);
      }
      setSending(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      setSending(false);
      throw err; // Re-throw to let the component handle it
    }
  }, [thread]);

  // Refresh messages
  const refreshMessages = useCallback(async () => {
    if (!thread) return;

    try {
      const messagesRes = await getThreadMessages(thread.id);

      if (messagesRes.success && messagesRes.data) {
        setMessages(messagesRes.data.messages || []);
      }
    } catch (err) {
      console.error('Error refreshing messages:', err);
      // Don't set error state for refresh failures
    }
  }, [thread]);

  // Auto-refresh messages every 30 seconds
  useEffect(() => {
    if (!thread) return;

    const interval = setInterval(() => {
      refreshMessages();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [thread, refreshMessages]);

  return {
    thread,
    messages,
    loading: loading || sending,
    error,
    sendMessage,
    refreshMessages,
  };
};